import React, { useState, useEffect } from "react";
import styles from "../../../styles/IntervalQuiz.module.css";
import { IntervalQuizOptions } from "./IntervalQuiz";
import ModeSelector, { QuizMode } from "../../../components/ModeSelector";
import Tooltip from "../../../components/Tooltip";
import { getIntervalReference } from "../../../data/intervalReferences";

interface IntervalPreQuizProps {
    setQuizOptions: (options: IntervalQuizOptions) => void;
    initialOptions?: IntervalQuizOptions;
}

// mapping interval names to semitone counts
const allIntervals = [
    { name: "Unison", semitones: 0 },
    { name: "Minor 2nd", semitones: 1 },
    { name: "Major 2nd", semitones: 2 },
    { name: "Minor 3rd", semitones: 3 },
    { name: "Major 3rd", semitones: 4 },
    { name: "Perfect 4th", semitones: 5 },
    { name: "Tritone", semitones: 6 },
    { name: "Perfect 5th", semitones: 7 },
    { name: "Minor 6th", semitones: 8 },
    { name: "Major 6th", semitones: 9 },
    { name: "Minor 7th", semitones: 10 },
    { name: "Major 7th", semitones: 11 },
    { name: "Octave", semitones: 12 },
];

export default function IntervalPreQuiz({ setQuizOptions, initialOptions }: IntervalPreQuizProps) {
    // Initialize from saved options, or use defaults
    const [selected, setSelected] = useState<string[]>(
        initialOptions?.availableIntervals?.map((i) => i.name) ?? ["Minor 3rd", "Major 3rd"]
    );
    const [mode, setMode] = useState<QuizMode>(initialOptions?.mode ?? "questions");
    const [duration, setDuration] = useState<number>(initialOptions?.timeLimitSeconds ?? 60);
    const [questionLimit, setQuestionLimit] = useState<number>(initialOptions?.questionLimit ?? 10);

    const toggleInterval = (interval: string) => {
        setSelected((prev) => {
            return prev.includes(interval)
                ? prev.filter((i) => i !== interval)
                : [...prev, interval];
        });
    };

    const toggleAll = () => {
        if (selected.length === allIntervals.length) {
            setSelected([]); // deselect all
        } else {
            setSelected(allIntervals.map((i) => i.name)); // select all
        }
    };

    // Whenever settings change, update parent quizOptions
    useEffect(() => {
        setQuizOptions({
            availableIntervals: allIntervals.filter((i) => selected.includes(i.name)),
            mode,
            timeLimitSeconds: mode === "time" ? duration : undefined,
            questionLimit: mode === "questions" ? questionLimit : undefined,
        });
    }, [selected, mode, duration, questionLimit, setQuizOptions]);

    return (
        <div className={styles.preQuizWrapper}>
            <ModeSelector value={mode} onChange={setMode} />

            {mode === "time" && (
                <div className={styles.drillOptionLabel}>
                    <label className={styles.comboLabel}>
                        Duration:{" "}
                        <input
                            type="number"
                            value={duration}
                            min={10}
                            max={600}
                            onChange={(e) => {
                                setDuration(Number(e.target.value));
                            }}
                            className={styles.preQuizCounter}
                        />
                    </label>
                </div>
            )}

            {mode === "questions" && (
                <div className={styles.drillOptionLabel}>
                    <label className={styles.comboLabel}>
                        Question limit:{" "}
                        <input
                            type="number"
                            value={questionLimit}
                            min={1}
                            max={100}
                            onChange={(e) => {
                                setQuestionLimit(Number(e.target.value));
                            }}
                            className={styles.preQuizCounter}
                        />
                    </label>
                </div>
            )}

            <div className={styles.selectAllWrapper}>
                <div>Intervals:</div>

                <button onClick={toggleAll} className={styles.preQuizButton}>
                    {selected.length === allIntervals.length ? "Deselect All" : "Select All"}
                </button>
            </div>

            <div className={styles.intervalsWrapper}>
                {allIntervals.map((interval) => {
                    const ref = getIntervalReference(interval.name);
                    const tooltipContent = ref ? (
                        <div className={styles.intervalHint}>
                            <div className={styles.intervalHintLabel}>Sounds like:</div>
                            <div className={styles.intervalHintSong}>
                                {ref.songExample}
                            </div>
                            <div className={styles.intervalHintCharacter}>
                                {ref.character}
                            </div>
                        </div>
                    ) : null;

                    return (
                        <Tooltip
                            key={interval.name}
                            content={tooltipContent}
                            position="top"
                        >
                            <label
                                className={
                                    selected.includes(interval.name)
                                        ? `${styles.intervalCheckboxWrapper} ${styles.intervalCheckboxWrapperChecked}`
                                        : styles.intervalCheckboxWrapper
                                }
                            >
                                {interval.name}
                                <input
                                    type="checkbox"
                                    checked={selected.includes(interval.name)}
                                    onChange={() => {
                                        toggleInterval(interval.name);
                                    }}
                                    className={styles.hiddenCheckbox}
                                />
                            </label>
                        </Tooltip>
                    );
                })}
            </div>
        </div>
    );
}
